import React from 'react';
import { DataGrid, GridColDef, GridRowId } from '@mui/x-data-grid';
import { Box, Typography, TextField, useMediaQuery } from '@mui/material';
import { MobileNumberField } from './MobileNumberField';
import { useTheme } from '@mui/material/styles';
import { NumberInputCell } from './NumberInputCell';
import { colors, typography } from '../theme';

type RecoveryRow = {
  id: string;
  suite: string;
  tenant_name: string;
  recovery_start_month: number;
  square_feet: number;
  rent_type?: string;
};

export default function RecoveryIncomeTableIndustrial({ retailIncome, setRetailIncome, expenses }: { retailIncome: RecoveryRow[]; setRetailIncome: React.Dispatch<React.SetStateAction<any[]>>; expenses?: any[] }) {
  const rows = (retailIncome || []).map((r: any) => ({ id: r.id, suite: r.suite, tenant_name: r.tenant_name, recovery_start_month: r.recovery_start_month, square_feet: Number(r.square_feet || 0), rent_type: r.rent_type || 'Gross' }));
  const totalSquareFeet = (retailIncome || []).reduce((sum: number, r: any) => sum + Number(r.square_feet || 0), 0);
  const baseRentAnnual = (retailIncome || []).reduce((sum: number, r: any) => {
    const psf = Number(r?.rent_per_square_foot_per_year || 0);
    const sf = Number(r?.square_feet || 0);
    return sum + psf * sf;
  }, 0);
  const retailExpenses = (expenses || []).filter((e: any) => (e?.type || '').toLowerCase() === 'retail');
  const computeAnnual = (e: any) => {
    const factor = String(e?.factor || '').toLowerCase();
    const cost = Number(e?.cost_per || 0);
    if (factor === 'annual') return cost;
    if (factor === 'per sf / yr.' || factor === 'per sf') return cost * Number(totalSquareFeet || 0);
    if (factor === 'percent of base rent') return (cost / 100) * Number(baseRentAnnual || 0);
    return cost;
  };
  const totalAnnualGrossIncluded = retailExpenses
    .filter((e: any) => ['both', 'gross'].includes(String(e?.rent_type_included || '').toLowerCase()))
    .reduce((sum: number, e: any) => sum + computeAnnual(e), 0);
  const totalAnnualNNNIncluded = retailExpenses
    .filter((e: any) => ['both', 'nnn'].includes(String(e?.rent_type_included || '').toLowerCase()))
    .reduce((sum: number, e: any) => sum + computeAnnual(e), 0);
  const perSfGross = totalSquareFeet ? totalAnnualGrossIncluded / totalSquareFeet : 0;
  const perSfNNN = totalSquareFeet ? totalAnnualNNNIncluded / totalSquareFeet : 0;

  const handleCellChange = (id: GridRowId, field: string, value: any) => {
    setRetailIncome((prev) =>
      prev.map((row: any) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'suite',
      headerName: 'Suite',
      flex: 0.8,
      minWidth: 140,
      sortable: false,
      cellClassName: 'greyed-out-cell',
      renderCell: (params) => <span>{params.value}</span>,
    },
    {
      field: 'tenant_name',
      headerName: 'Tenant Name',
      flex: 1.2,
      minWidth: 220,
      sortable: false,
      cellClassName: 'greyed-out-cell',
      renderCell: (params) => <span>{params.value}</span>,
    },
    {
      field: 'rent_type',
      headerName: 'Rent Type',
      flex: 0.8,
      minWidth: 110,
      sortable: false,
      cellClassName: 'greyed-out-cell',
      renderCell: (params) => <span>{params.value}</span>,
    },
    {
      field: 'recovery_start_month',
      headerName: 'Recovery Start Month',
      flex: 1,
      minWidth: 170,
      sortable: false,
      type: 'number',
      renderCell: (params) => (
        <NumberInputCell
          params={params}
          handleCellChange={handleCellChange}
          field="recovery_start_month"
          prefix="Month"
        />
      )
    },
    {
      field: 'pro_rata_share',
      headerName: 'Pro Rata Share',
      flex: 1,
      minWidth: 160,
      sortable: false,
      cellClassName: 'greyed-out-cell',
      renderCell: (params) => {
        const sf = Number(params?.row?.square_feet ?? 0);
        const share = totalSquareFeet ? sf / totalSquareFeet : 0;
        const formatted = share.toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return <span>{formatted}</span>;
      }
    },
    {
      field: 'rec_per_sf_year',
      headerName: 'Rec. / SF / Yr.',
      flex: 1,
      minWidth: 150,
      sortable: false,
      cellClassName: 'greyed-out-cell',
      renderCell: (params) => {
        const tenantType = String(params?.row?.rent_type || 'Gross').toLowerCase();
        const perSf = tenantType === 'nnn' ? perSfNNN : perSfGross;
        const formatted = `$${Number(perSf).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        return <span>{formatted}</span>;
      }
    },
    {
      field: 'annual_recovery',
      headerName: 'Annual Recovery',
      flex: 1,
      minWidth: 170,
      sortable: false,
      cellClassName: 'greyed-out-cell',
      renderCell: (params) => {
        const sf = Number(params?.row?.square_feet ?? 0);
        const share = totalSquareFeet ? sf / totalSquareFeet : 0;
        const tenantType = String(params?.row?.rent_type || 'Gross').toLowerCase();
        const allowedTotal = tenantType === 'nnn' ? totalAnnualNNNIncluded : totalAnnualGrossIncluded;
        const annual = share * Number(allowedTotal || 0);

        const formatted = `$${Number(annual).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
        return <span>{formatted}</span>;
      }
    },
  ];

  const getRowClassName = (params: any) =>
    params.indexRelativeToCurrentPage % 2 === 0 ? 'u-row-odd' : 'u-row-even';

  const Footer = () => {
    // Average Rec. / SF / Yr. and Total Annual Recovery
    const totals = rows.reduce(
      (acc: any, r: any) => {
        const sf = Number(r.square_feet || 0);
        const share = totalSquareFeet ? sf / totalSquareFeet : 0;
        const tenantType = String(r?.rent_type || 'Gross').toLowerCase();
        const allowedTotal = tenantType === 'nnn' ? totalAnnualNNNIncluded : totalAnnualGrossIncluded;
        const annual = share * Number(allowedTotal || 0);
        acc.totalAnnual += annual;
        acc.totalSF += sf;
        return acc;
      },
      { totalAnnual: 0, totalSF: 0 }
    );
    const avgPerSf = totals.totalSF ? totals.totalAnnual / totals.totalSF : 0;
    return (
      <Box
        sx={{
          p: 2,
          backgroundColor: colors.white,
          borderTop: `1px solid ${colors.grey[300]}`,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 3,
          fontSize: 14.5,
          color: colors.grey[700],
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ textAlign: 'right', fontWeight: 600 }}>
          Average Rec. / SF / Yr.:{' '}
          <Box component="span" sx={{ fontWeight: 700, color: colors.grey[900] }}>
            ${avgPerSf.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right', fontWeight: 600 }}>
          Total Annual Recovery:{' '}
          <Box component="span" sx={{ fontWeight: 700, color: colors.grey[900] }}>
            ${Math.round(totals.totalAnnual).toLocaleString()}
          </Box>
        </Box>
      </Box>
    );
  };

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const renderField = (label: string, value: React.ReactNode) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ color: '#6b7280', fontSize: '0.62rem', display: 'block' }}>
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 600, color: '#1f2937', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value}
      </Typography>
    </Box>
  );

  if (isMobile) {
    let mTotalAnnual = 0;
    let mTotalSF = 0;
    rows.forEach((r: any) => {
      const sf = Number(r.square_feet || 0);
      const share = totalSquareFeet ? sf / totalSquareFeet : 0;
      const tenantType = String(r?.rent_type || 'Gross').toLowerCase();
      const allowedTotal = tenantType === 'nnn' ? totalAnnualNNNIncluded : totalAnnualGrossIncluded;
      mTotalAnnual += share * Number(allowedTotal || 0);
      mTotalSF += sf;
    });
    const mAvgPerSf = mTotalSF ? mTotalAnnual / mTotalSF : 0;

    return (
      <Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {rows.map((row: any) => {
            const sf = Number(row.square_feet || 0);
            const share = totalSquareFeet ? sf / totalSquareFeet : 0;
            const tenantType = String(row?.rent_type || 'Gross').toLowerCase();
            const perSf = tenantType === 'nnn' ? perSfNNN : perSfGross;
            const allowedTotal = tenantType === 'nnn' ? totalAnnualNNNIncluded : totalAnnualGrossIncluded;
            const annual = share * Number(allowedTotal || 0);
            return (
              <Box key={row.id} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2, bgcolor: '#fff' }}>
                <Box sx={{ minWidth: 0, mb: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.suite ? `Suite ${row.suite}` : 'Suite —'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.tenant_name || 'No tenant'} · {row.rent_type || 'Gross'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mb: 2 }}>
                  {renderField('Pro Rata Share', share.toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 }))}
                  {renderField('Rec. / SF / Yr.', `$${perSf.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)}
                  {renderField('Annual Recovery', `$${annual.toLocaleString(undefined, { maximumFractionDigits: 0 })}`)}
                </Box>
                <MobileNumberField
                  label="Recovery Start Month" type="number" size="small" fullWidth
                  value={row.recovery_start_month ?? ''}
                  onChange={(e) => handleCellChange(row.id, 'recovery_start_month', Number(e.target.value))}
                />
              </Box>
            );
          })}
        </Box>

        <Box sx={{ textAlign: 'right', fontSize: 13, color: '#1f2937', mt: 2 }}>
          <div><span style={{ fontWeight: 600 }}>Average Rec. / SF / Yr.:</span> ${mAvgPerSf.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div><span style={{ fontWeight: 600 }}>Total Annual Recovery:</span> ${Math.round(mTotalAnnual).toLocaleString()}</div>
        </Box>
      </Box>
    );
  }

  return (
    <DataGrid
      autoHeight
      disableColumnMenu
      disableColumnFilter
      disableColumnSelector
      disableColumnSorting
      rows={rows}
      columns={columns}
      disableRowSelectionOnClick
      sortingOrder={[]}
      rowHeight={52}
      getRowClassName={getRowClassName}
      hideFooterSelectedRowCount
      slots={{ footer: Footer }}
      sx={{
        minWidth: '900px',
        background: colors.white,
        border: `1px solid ${colors.grey[300]}`,
        borderRadius: 2,
        fontFamily: typography.fontFamily,
        fontSize: 14.5,
        '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-cellContent': {
          fontFamily: typography.fontFamily,
          fontSize: 14.5,
        },
        '& .MuiDataGrid-main': { background: colors.white },
        '& .MuiDataGrid-columnHeaders': { background: colors.white, minHeight: 52, maxHeight: 52, borderBottom: `1px solid ${colors.grey[300]}` },
        '& .MuiDataGrid-columnHeader': { background: colors.white, minHeight: 52, maxHeight: 52 },
        '& .MuiDataGrid-columnHeaderTitleContainer': { background: colors.white },
        '& .MuiDataGrid-columnHeaderTitle': {
          fontWeight: 600,
          fontSize: 14.5,
          fontFamily: typography.fontFamily,
          textTransform: 'none',
          lineHeight: '52px',
          color: colors.grey[900],
        },
        '& .MuiDataGrid-cell': { borderBottom: `1px solid ${colors.grey[300]}`, background: colors.white, fontSize: 14.5, color: colors.grey[900] },
        '& .greyed-out-cell': { color: colors.grey[600] },
        '& .u-editable-input': { border: 'none', borderBottom: '2px solid transparent', borderRadius: 0, background: 'transparent', fontWeight: 600 },
        '& .MuiDataGrid-row:hover .u-editable-input, & .u-editable-input:focus': { borderBottom: `2px solid ${colors.blue} !important` },
        '& .MuiDataGrid-row': { background: colors.white },
        '& .u-row-even': { background: colors.grey[50] },
        '& .MuiDataGrid-row:hover': { backgroundColor: colors.blueTint },
        '& .MuiDataGrid-virtualScroller': { background: colors.white },
        '& .MuiDataGrid-footerContainer': { background: colors.white }
      }}
    />
  );
}


